/**
 * Contrastul culorii alese de client, derivat o singură dată și folosit în două
 * locuri care TREBUIE să arate la fel: widgetul real (`sdk/src/widget.ts`, unde
 * rezultatul ajunge în `--aig-on-primary`/`--aig-primary-ink`) și previzualizarea
 * din dashboard (`WidgetPreviewMock`). Dacă ar exista două implementări, exact
 * cazul pastel — singurul unde decizia contează — ar diverge între ele.
 *
 * Fără dependențe (nici zod): dashboardul importă din acest modul, iar restul lui
 * `shared` ar trage bundlerul după el degeaba.
 */

/** `#rrggbb` → triplet `[r, g, b]` (0–255). Baza nuanțelor translucide (soft tint)
 *  derivate dintr-o cerneală de orice culoare, nu doar alb/negru. */
export function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

/** Luminanța relativă WCAG a unui hex `#rrggbb`. */
export function relativeLuminance(hex: string): number {
  const n = parseInt(hex.slice(1), 16);
  const chan = [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff].map((c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * chan[0]! + 0.7152 * chan[1]! + 0.0722 * chan[2]!;
}

export function contrastRatio(hexA: string, hexB: string): number {
  const [hi, lo] = [relativeLuminance(hexA), relativeLuminance(hexB)].sort((x, y) => y - x);
  return (hi! + 0.05) / (lo! + 0.05);
}

/** Nuanță mai închisă, fără `color-mix` (suport larg de browser). */
export function darkenHex(hex: string, amount: number): string {
  const n = parseInt(hex.slice(1), 16);
  const clamp = (c: number): number => Math.max(0, Math.min(255, c));
  const r = clamp(((n >> 16) & 0xff) * (1 - amount));
  const g = clamp(((n >> 8) & 0xff) * (1 - amount));
  const b = clamp((n & 0xff) * (1 - amount));
  return `#${[r, g, b].map((c) => Math.round(c).toString(16).padStart(2, "0")).join("")}`;
}

/**
 * Cerneala așezată PESTE culoarea clientului (header, bula vizitatorului). Alb pe
 * un galben deschis dădea 1.53:1 — sub o treime din pragul AA de 4.5:1, adică text
 * practic ilizibil. Alegem varianta cu contrast mai bun, nu una fixă.
 */
export function inkOnPrimary(hex: string): string {
  return contrastRatio(hex, "#ffffff") >= contrastRatio(hex, "#111111") ? "#ffffff" : "#111111";
}

/**
 * Aceeași culoare, dar folosită ca TEXT pe alb (butonul Send, linia de accent a
 * compozitorului): acolo un pastel se pierde în fundal, așa că o închidem în pași
 * până trece pragul AA. Culorile deja suficient de închise ies neatinse.
 */
export function inkOnSurface(hex: string): string {
  let candidate = hex;
  for (let i = 0; i < 20 && contrastRatio(candidate, "#ffffff") < 4.5; i++) {
    candidate = darkenHex(candidate, 0.12);
  }
  return candidate;
}
